import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { findById, update } from "@/apis/subscription";
import {
  SubscriptionProductDto,
  SubscriptionProductCreateRequestDto,
} from "@/dto/SubscriptionDto";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useModal } from "mui-modal-provider";
import { Confirm } from "@/organism/Confirm";
import { Alert } from "@/organism/Alert";
import { ErrorDto } from "@/dto/ErrorDto";
import Router from "@/constants/router";

export default function useDetail() {
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [subscriptionInfo, setSubscriptionInfo] =
    useState<SubscriptionProductDto | null>(null);
  const navigate = useNavigate();
  const { showModal } = useModal();

  /**
   * 도장 정보 조회
   */
  const fetchCompanyData = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        const data = await findById(id);
        setSubscriptionInfo(data.data);
      } catch (e) {
        console.error(e);
        navigate(Router.Subscription);
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (id) {
      fetchCompanyData(id);
    }
  }, [id, fetchCompanyData]);

  /**
   * 구독 상품 수정 이벤트 핸들러
   */
  const handleSubscriptionUpdate = async (
    subscription: SubscriptionProductCreateRequestDto,
    id: string
  ) => {
    const modal = showModal(Confirm, {
      title: "구독 상품 수정",
      content: "구독 상품을 수정하시겠습니까?",
      onOk: async () => {
        modal.destroy();
        try {
          setIsLoading(true);
          const result = await update(id, subscription);
          if (result.success) {
            enqueueSnackbar("구독 상품이 수정되었습니다.", {
              variant: "success",
            });
            if (id) {
              await fetchCompanyData(id);
            }
          } else {
            showModal(Alert, {
              title: "오류",
              content: result.message,
              onClose: () => {},
            });
          }
        } catch (error) {
          const e = error as ErrorDto;
          showModal(Alert, {
            title: "오류",
            content:
              e.details?.errorMessage || "구독 상품 수정에 실패했습니다.",
            onClose: () => {},
          });
        } finally {
          setIsLoading(false);
        }
      },
      onCancel: () => {
        modal.destroy();
      },
    });
  };

  return {
    isLoading,
    subscriptionInfo,
    handleSubscriptionUpdate,
  };
}
